/**
 * Exit / Harvest Watchlist — pure logic behind the Risk dashboard card.
 *
 * Extracted from RiskDashboard.tsx so the part that makes a CLAIM ABOUT A POSITION
 * is unit-testable (same pattern as navPerformance.ts). Two things live here:
 *
 *   1. CONTRACT IDENTITY. A watchlist line used to render as "SPX 2028-12-15", which
 *      does not identify a contract — the ladder holds several strikes on the same
 *      expiry. strike/right were always in the get_portfolio_view payload; they were
 *      simply never read.
 *
 *   2. WHY A LINE IS FLAGGED. The backend's `decision_triggers` array is the sole
 *      AUTHORITY for why a position is on the list (portfolio_view.py
 *      _compose_position_decision). Nothing here re-derives a flag — each explainer
 *      takes a trigger the backend already emitted and attaches the numbers that
 *      produced it, drawn from the same payload row. Thresholds come from the
 *      payload's `decisionThresholds`, never a hardcoded copy.
 *
 * Advisory only. Nothing in this module decides anything or triggers a trade.
 *
 * Run the tests:
 *   cd HelionDashboard && CI=true npx craco test --testPathPattern exitWatchlist --watchAll=false
 */

/* ------------------------------ formatting ------------------------------ */

export const isNum = (x: unknown): x is number =>
  typeof x === "number" && Number.isFinite(x);

export function fmtUSD(x?: number | null, maxFractionDigits = 0) {
  if (!isNum(x)) return "—";
  return x.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: maxFractionDigits,
  });
}

export function fmtPct(x?: number | null, digits = 1) {
  if (!isNum(x)) return "—";
  return `${(x * 100).toFixed(digits)}%`;
}

export function fmtNum(x?: number | null, digits = 2) {
  if (!isNum(x)) return "—";
  return x.toFixed(digits);
}


/* ------------------------------ types: harvest policy ------------------------------ */

// The live home-run harvest policy, echoed verbatim by get_portfolio_view
// (strategy/home_run_redeployment.load_home_run_policy). Snake_case because the
// backend returns the policy dict as-is rather than camelizing it.
//
// This is the SOURCE OF THE THRESHOLD the watchlist explains: a position is
// is_home_run when return_pct >= trigger_multiple - 1 (portfolio_view.py:1815,
// :1421). We read it only to SHOW the number the backend already compared
// against — the flag itself is never recomputed here.
export type RiskHomeRunPolicy = {
  enabled?: boolean | null;
  trigger_multiple?: number | null;
  first_trim_pct?: number | null;
  runner_floor_return_pct?: number | null;
  redeploy_delay_days?: number | null;
  // The DERIVED exit-time gate (backend 2026-09-09). Preferred over
  // min_initial_contracts, which is a construction rule and ignores the trim pct.
  min_contracts_for_trim?: number | null;
  min_initial_contracts?: number | null;
  manual_execution_required?: boolean | null;
};

/* ------------------------------ types: watchlist row ------------------------------ */

// One held-LEAPS line from get_portfolio_view, normalized for the exit watchlist.
//
// IDENTITY (right/strike/…): a watchlist line used to render as "SPX 2028-12-15",
// which does not identify a contract — the book holds several strikes on the same
// expiry, so the CIO could not tell which one the model was flagging. strike and
// right are already in the payload (portfolio_view.py:1839-1840); they were simply
// never read. Same for the priced fields below, which turn "why flagged" from a
// four-word label into the arithmetic behind it.
export type WatchlistRow = {
  contractId: string;
  symbol: string;
  expiration: string;
  right: string | null;
  strike: number | null;
  displayName: string | null;
  contractDesc: string | null;
  dte: number | null;
  ageDays: number | null;
  quantity: number | null;
  multiplier: number | null;
  entryPrice: number | null;
  markPrice: number | null;
  // The stored mark return_pct is DERIVED FROM. markPrice above comes off a
  // different, routinely fresher COALESCE chain, so the two disagree by 1-4%
  // across the book. Quoting markPrice beside the return let a reader do
  // arithmetic that did not come out; the harvest sentence uses this instead.
  returnBasisPrice: number | null;
  marketValue: number | null;
  underlyingPrice: number | null;
  returnPct: number | null;
  peakReturn: number | null;
  ddFromPeak: number | null;
  delta: number | null;
  vega: number | null;
  impliedVol: number | null;
  posture: string | null;
  reason: string | null;
  triggers: string[];
  detail: string | null;
  hardExit: boolean;
  hardExitEvaluated: boolean;
  likelyPeaked: boolean;
  likelyMature: boolean;
  isHomeRun: boolean;
  // False => return_pct or the harvest policy was unavailable, so the harvest test
  // never ran. Mirrors hardExitEvaluated: "not evaluated" must never render as
  // "healthy hold", because that failure runs in the reassuring direction.
  homeRunEvaluated: boolean;
  exitHintScore: number | null;
  exitHintPercentile: number | null;
  exitRankOf: number | null;
  priority: number;
  // Harvest-policy overlay (portfolio_view.py:1928-1941) — present only on lines the
  // live policy actually treats as harvest candidates.
  homeRunCandidate: boolean;
  homeRunTrimQty: number | null;
  homeRunRunnerQty: number | null;
  homeRunHarvestNotional: number | null;
  homeRunCanPartialTrim: boolean | null;
  homeRunSingleContract: boolean | null;
  homeRunLockActive: boolean;
  homeRunUnlockAtUtc: string | null;
};

/* ------------------------------ normalization + gating ------------------------------ */

// ---- Exit / harvest watchlist (from get_portfolio_view heldLeaps) ----

export const WATCHLIST_PCTL_GATE = 80;
// The exit model's posture (decision_posture) is the trustworthy discriminator.
// The raw exit_hint_score has a high floor (it is essentially scaled drawdown,
// ~0.13+ for the whole book), so it is intentionally NOT used as an absolute
// gate — doing so flags every holding.
export const ACTIONABLE_POSTURES = ["act now", "review today", "monitor closely"];

export function pick(row: any, ...names: string[]): any {
  for (const n of names) {
    if (row && row[n] !== undefined && row[n] !== null) return row[n];
  }
  return undefined;
}

export function asNum(v: any): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
}

export function asStr(v: any): string | null {
  if (v === undefined || v === null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

export function normalizeWatchlistRow(raw: any): WatchlistRow {
  const triggersRaw = pick(raw, "decision_triggers", "decisionTriggers");
  const triggers = Array.isArray(triggersRaw)
    ? triggersRaw.map((t) => String(t)).filter(Boolean)
    : [];
  return {
    contractId: String(pick(raw, "contract_id", "contractId", "conid") ?? "—"),
    symbol: String(pick(raw, "symbol") ?? "SPX"),
    expiration: String(pick(raw, "expiration", "expiry") ?? "—"),
    right: asStr(pick(raw, "right", "put_call", "putCall")),
    strike: asNum(pick(raw, "strike")),
    displayName: asStr(pick(raw, "display_name", "displayName")),
    contractDesc: asStr(pick(raw, "contract_desc", "contractDesc")),
    dte: asNum(pick(raw, "dte")),
    ageDays: asNum(pick(raw, "age_days", "ageDays")),
    quantity: asNum(pick(raw, "quantity", "qty")),
    multiplier: asNum(pick(raw, "multiplier")),
    entryPrice: asNum(pick(raw, "entry_price", "entryPrice")),
    // `mid` is the cleaner mark when both sides are quoted; market_price is the
    // last trade and on a thin long-dated line can be hours old.
    markPrice: asNum(pick(raw, "mid", "market_price", "marketPrice")),
    returnBasisPrice: asNum(pick(raw, "return_basis_price", "returnBasisPrice")),
    marketValue: asNum(pick(raw, "market_value", "marketValue")),
    underlyingPrice: asNum(pick(raw, "underlying_price", "underlyingPrice")),
    returnPct: asNum(pick(raw, "return_pct", "returnPct")),
    peakReturn: asNum(pick(raw, "peak_return", "peakReturn")),
    ddFromPeak: asNum(pick(raw, "drawdown_from_peak", "ddFromPeak", "drawdownFromPeak")),
    delta: asNum(pick(raw, "delta")),
    vega: asNum(pick(raw, "vega")),
    impliedVol: asNum(pick(raw, "implied_vol", "impliedVol")),
    posture: (pick(raw, "decision_posture", "decisionPosture") as string) ?? null,
    reason:
      (pick(raw, "hard_exit_reason", "hardExitReason") as string) ||
      (pick(raw, "exit_hint_reason", "exitHintReason") as string) ||
      null,
    triggers,
    // The backend already composes a full sentence + context string
    // (portfolio_view.py:1533-1535) and has always returned it; nothing rendered it.
    detail: asStr(pick(raw, "decision_detail", "decisionDetail")),
    hardExit: Boolean(pick(raw, "hard_exit", "hardExit")),
    // False => NOTHING evaluates hard_sell, so an absent hard exit means
    // "not evaluated", not "clear". See HARD_EXIT_EVALUATED in portfolio_view.py.
    hardExitEvaluated: Boolean(pick(raw, "hard_exit_evaluated", "hardExitEvaluated")),
    likelyPeaked: Boolean(pick(raw, "likely_peaked", "likelyPeaked")),
    likelyMature: Boolean(pick(raw, "likely_mature", "likelyMature")),
    // is_home_run_live FIRST: the raw `is_home_run` column has no writer and is
    // always false, and pick() treats a literal false as present — so reading the
    // raw key first made this permanently false and the "Home run" badge dead code,
    // even on a line whose own trigger text said "winner worth harvesting".
    isHomeRun: Boolean(
      pick(raw, "is_home_run_live", "isHomeRunLive", "home_run_candidate", "is_home_run", "isHomeRun")
    ),
    homeRunEvaluated: Boolean(pick(raw, "home_run_evaluated", "homeRunEvaluated")),
    exitHintScore: asNum(pick(raw, "exit_hint_score", "exitHintScore")),
    exitHintPercentile: asNum(pick(raw, "exit_hint_percentile", "exitHintPercentile")),
    exitRankOf: asNum(pick(raw, "exit_risk_rank_of", "exitRiskRankOf")),
    priority: asNum(pick(raw, "decision_priority", "decisionPriority")) ?? 0,
    homeRunCandidate: Boolean(pick(raw, "home_run_candidate", "homeRunCandidate")),
    homeRunTrimQty: asNum(pick(raw, "home_run_suggested_trim_qty", "homeRunSuggestedTrimQty")),
    homeRunRunnerQty: asNum(pick(raw, "home_run_suggested_runner_qty", "homeRunSuggestedRunnerQty")),
    homeRunHarvestNotional: asNum(
      pick(raw, "home_run_suggested_harvest_notional", "homeRunSuggestedHarvestNotional")
    ),
    homeRunCanPartialTrim:
      pick(raw, "home_run_can_partial_trim", "homeRunCanPartialTrim") === undefined
        ? null
        : Boolean(pick(raw, "home_run_can_partial_trim", "homeRunCanPartialTrim")),
    homeRunSingleContract:
      pick(raw, "home_run_single_contract_constraint", "homeRunSingleContractConstraint") === undefined
        ? null
        : Boolean(pick(raw, "home_run_single_contract_constraint", "homeRunSingleContractConstraint")),
    homeRunLockActive: Boolean(pick(raw, "home_run_lock_active", "homeRunLockActive")),
    homeRunUnlockAtUtc: asStr(pick(raw, "home_run_unlock_at_utc", "homeRunUnlockAtUTC")),
  };
}

// Include a position only when the exit model itself flags it: its posture call
// (Review today / Monitor closely / Act now), or a hard-exit / peaked / home-run
// signal. Falls back to relative exit-rank only when posture is absent. The raw
// exit_hint_score is deliberately NOT a gate (high floor => flags everything).
export function buildWatchlist(rawRows: any[] | null | undefined): WatchlistRow[] {
  if (!Array.isArray(rawRows)) return [];
  return rawRows
    .map(normalizeWatchlistRow)
    .filter((r) => {
      if (r.hardExit || r.likelyPeaked || r.isHomeRun) return true;
      if (ACTIONABLE_POSTURES.includes((r.posture || "").toLowerCase())) return true;
      // Fallback only when the model gave no posture: relative exit-rank in the book.
      if (!r.posture && isNum(r.exitHintPercentile) && (r.exitHintPercentile as number) >= WATCHLIST_PCTL_GATE) {
        return true;
      }
      return false;
    })
    .sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority;
      return (b.exitHintPercentile ?? 0) - (a.exitHintPercentile ?? 0);
    });
}

/* ------------------------------ contract identity ------------------------------ */

/* ---------------- Contract identity ----------------
 * Same convention as the Portfolio page (PortfolioView.tsx fmtStrike /
 * fmtContractLabel) so one contract reads identically on every screen:
 *   SPX 2028-12-15 C 6000
 * Expiration alone is NOT an identity — the ladder holds several strikes per
 * expiry, which is what made the old "SPX 2028-12-15" line unactionable.
 */
export function fmtStrike(strike: number | null | undefined): string {
  if (typeof strike !== "number" || !isFinite(strike)) return "—";
  const isHalf =
    Math.abs(strike * 2 - Math.round(strike * 2)) < 1e-9 &&
    Math.abs(strike - Math.round(strike)) > 1e-9;
  if (isHalf) return strike.toFixed(1);
  return Math.round(strike).toString();
}

// Structured fields first, then whatever label the broker/view composed. Never
// returns blank: an unresolved contract must read as an unresolved contract, not
// as an empty cell.
export function watchlistContractLabel(r: WatchlistRow): string {
  if (r.symbol && r.expiration && r.expiration !== "—" && r.right && isNum(r.strike)) {
    return `${r.symbol} ${r.expiration} ${r.right.toUpperCase().slice(0, 1)} ${fmtStrike(r.strike)}`;
  }
  if (r.displayName) return r.displayName;
  if (r.contractDesc) return r.contractDesc;
  const base = [r.symbol, r.expiration !== "—" ? r.expiration : null].filter(Boolean).join(" ");
  return base || (r.contractId !== "—" ? `conid ${r.contractId}` : "Unresolved contract");
}

// True when the label above had to fall back — i.e. the strike is genuinely
// missing from the payload rather than merely unrendered. The UI says so
// explicitly instead of quietly showing a partial identity.
export function hasFullContractIdentity(r: WatchlistRow): boolean {
  return Boolean(r.symbol && r.expiration && r.expiration !== "—" && r.right && isNum(r.strike));
}

/* ------------------------------ why-flagged explainers ------------------------------ */

/* ---------------- "Why flagged", with the arithmetic ----------------
 * The backend's decision_triggers array is the AUTHORITY for why a line is on
 * this list (portfolio_view.py _compose_position_decision). Nothing below
 * re-derives a flag: each entry takes a trigger the backend already emitted and
 * attaches the numbers that produced it, using values from the same payload.
 * A trigger we do not have an explainer for still renders, verbatim.
 */
export type FlagExplanation = {
  key: string;
  headline: string;
  detail: string;
  tone: "danger" | "elevated" | "warning" | "neutral";
  // Mirrors the priority the backend branch assigns (portfolio_view.py
  // _compose_position_decision). The backend appends triggers in EVALUATION order,
  // not importance order — harvest (80) is appended after off-peak (68) and maturity
  // (72) — so the collapsed cell must re-sort before truncating or it can hide the
  // very reason the line is on the list.
  priority: number;
};

// Thresholds the backend compared against, echoed in the payload so this page
// never hardcodes a copy that can drift from the engine.
export type DecisionThresholds = {
  givebackFromPeak?: number | null;
  dteReviewDays?: number | null;
  dteMonitorDays?: number | null;
  homeRunTriggerMultiple?: number | null;
  homeRunTriggerReturn?: number | null;
};

export function fmtMultiple(v: number | null | undefined): string {
  return isNum(v) ? `${(v as number).toFixed(2)}×` : "—";
}

export function explainFlags(
  r: WatchlistRow,
  thresholds: DecisionThresholds,
  policy: RiskHomeRunPolicy | null
): FlagExplanation[] {
  const out: FlagExplanation[] = [];
  const triggerMultiple =
    thresholds.homeRunTriggerMultiple ??
    (isNum(policy?.trigger_multiple) ? (policy?.trigger_multiple as number) : null);

  for (const t of r.triggers) {
    const lower = t.toLowerCase();

    if (lower.includes("order staged")) {
      out.push({
        key: t,
        headline: t,
        detail:
          "An exit ticket for this contract is queued and waiting on CIO approval. " +
          "Nothing executes until you approve it, and execution is manual in TWS.",
        tone: "danger",
        priority: 100,
      });
      continue;
    }

    if (lower.includes("hard exit")) {
      out.push({
        key: t,
        headline: "Hard-exit condition is live",
        detail: r.reason || "The forced-exit rule fired on this line. Review before the next session.",
        tone: "danger",
        priority: 100,
      });
      continue;
    }

    if (lower.includes("winner worth harvesting")) {
      const mult = isNum(r.returnPct) ? 1 + (r.returnPct as number) : null;
      const parts: string[] = [];
      parts.push(
        isNum(mult) && isNum(triggerMultiple)
          ? `The position is worth ${fmtMultiple(mult)} what it cost (${fmtPct(r.returnPct, 1)}), which is at or past the ${fmtMultiple(triggerMultiple)} home-run trigger in the harvest policy.`
          : `The position's return is at or past the home-run trigger in the harvest policy${isNum(mult) ? ` (now ${fmtMultiple(mult)} cost)` : ""}.`
      );
      // Quote the basis the RETURN was computed from, so the arithmetic on screen
      // reconciles. Where the live mark differs materially, say so rather than let
      // two numbers on one card silently disagree.
      const basis = isNum(r.returnBasisPrice) ? r.returnBasisPrice : r.markPrice;
      if (isNum(r.entryPrice) && isNum(basis)) {
        parts.push(`Paid ${fmtUSD(r.entryPrice, 2)} per contract, valued at ${fmtUSD(basis, 2)}.`);
        if (
          isNum(r.markPrice) &&
          isNum(r.returnBasisPrice) &&
          Math.abs((r.markPrice as number) - (r.returnBasisPrice as number)) /
            Math.max(Math.abs(r.returnBasisPrice as number), 1e-9) >
            0.005
        ) {
          parts.push(
            `The latest quote is ${fmtUSD(r.markPrice, 2)}; the return above is measured off the stored mark, not this quote.`
          );
        }
      }
      if (r.homeRunSingleContract) {
        parts.push(
          `A partial trim is not available: the policy needs at least ${policy?.min_contracts_for_trim ?? policy?.min_initial_contracts ?? 2} contracts to split a position, and this line holds ${isNum(r.quantity) ? r.quantity : "fewer"}. It is a hold-or-close decision.`
        );
      } else if (isNum(r.homeRunTrimQty) && (r.homeRunTrimQty as number) > 0) {
        parts.push(
          `Policy shape: trim ${r.homeRunTrimQty} and keep ${r.homeRunRunnerQty ?? "—"} running` +
            (isNum(r.homeRunHarvestNotional)
              ? `, about ${fmtUSD(r.homeRunHarvestNotional, 0)} of proceeds.`
              : ".")
        );
      }
      if (r.homeRunLockActive) {
        parts.push(
          `A redeployment lockout is already active on this contract${r.homeRunUnlockAtUtc ? ` until ${r.homeRunUnlockAtUtc}` : ""}.`
        );
      }
      out.push({
        key: t,
        headline: "Winner past the home-run trigger",
        detail: parts.join(" "),
        tone: "elevated",
        priority: 80,
      });
      continue;
    }

    if (lower.includes("likely peaked")) {
      out.push({
        key: t,
        headline: "Exit model calls it peaked",
        detail:
          `The exit model's peak flag is set${isNum(r.peakReturn) ? `; best return recorded was ${fmtPct(r.peakReturn, 1)}` : ""}${isNum(r.ddFromPeak) ? `, currently ${fmtPct(Math.abs(r.ddFromPeak as number), 1)} below it` : ""}. ` +
          "This flag has NOT been validated as a profitable action signal — treat it as a prompt to look, not a reason to sell.",
        tone: "warning",
        priority: 85,
      });
      continue;
    }

    if (lower.includes("runway is getting short")) {
      out.push({
        key: t,
        headline: "Aged into the maturity window",
        detail:
          `Held ${isNum(r.ageDays) ? `${r.ageDays} days` : "a long time"} with ${isNum(r.dte) ? `${r.dte} days` : "limited time"} left. ` +
          "A position this far through its life expresses less of the long-horizon convexity it was bought for.",
        tone: "warning",
        priority: 72,
      });
      continue;
    }

    const dteMatch = t.match(/^(\d+)\s*DTE left$/i);
    if (dteMatch) {
      const ladder = [thresholds.dteReviewDays, thresholds.dteMonitorDays]
        .filter((x) => isNum(x))
        .map((x) => `${x}d`)
        .join(" / ");
      out.push({
        key: t,
        headline: `${dteMatch[1]} days to expiry`,
        detail:
          `Remaining life has crossed the escalation ladder${ladder ? ` (${ladder})` : ""}. ` +
          "Decide whether to roll out to fresh tenor or close before time decay accelerates.",
        tone: "warning",
        priority:
          isNum(r.dte) && isNum(thresholds.dteReviewDays) && (r.dte as number) <= (thresholds.dteReviewDays as number)
            ? 78
            : 60,
      });
      continue;
    }

    // Information, not an instruction (CIO, 2026-09-02): the 36-year calibration found
    // no giveback level that predicts recovery, and acting on the flag failed the
    // backtest bar. The threshold printed here is the one the backend applied
    // (decisionThresholds), never a literal; the trigger format "NN% off peak" is the
    // contract this regex parses.
    const offPeak = t.match(/^(\d+)% off peak$/i);
    if (offPeak) {
      out.push({
        key: t,
        headline: `Gave back ${offPeak[1]}% of its peak`,
        detail:
          `Peaked at ${isNum(r.peakReturn) ? fmtPct(r.peakReturn, 1) : "—"} and is now at ${isNum(r.returnPct) ? fmtPct(r.returnPct, 1) : "—"}` +
          (isNum(thresholds.givebackFromPeak)
            ? `, past the ${fmtPct(thresholds.givebackFromPeak, 0)} giveback line.`
            : ".") +
          " Giveback is measured against the position's own high-water mark, not the index." +
          " Information, not an instruction: no giveback level predicted recovery in 36 years of held LEAPS, and acting on this flag failed the backtest bar.",
        tone: "warning",
        priority: 68,
      });
      continue;
    }

    out.push({ key: t, headline: t, detail: "", tone: "neutral", priority: 0 });
  }

  // Highest-priority reason first, so a truncated view never drops the real one.
  // Stable within a priority: evaluation order is preserved for ties.
  return out
    .map((e, i) => ({ e, i }))
    .sort((a, b) => (b.e.priority - a.e.priority) || (a.i - b.i))
    .map((x) => x.e);
}

// The rules that were CHECKED and did not fire. "Why this line and not that one"
// is half the answer, and without it a single trigger reads as the whole model.
export function explainNonTriggers(r: WatchlistRow, thresholds: DecisionThresholds): string[] {
  const out: string[] = [];
  const fired = r.triggers.join(" ").toLowerCase();

  if (isNum(r.ddFromPeak) && isNum(thresholds.givebackFromPeak) && !fired.includes("off peak")) {
    out.push(
      `giveback ${fmtPct(Math.abs(r.ddFromPeak as number), 1)} vs ${fmtPct(thresholds.givebackFromPeak, 0)} line`
    );
  }
  if (isNum(r.dte) && isNum(thresholds.dteMonitorDays) && !/\d+ DTE left/i.test(r.triggers.join(" "))) {
    out.push(`${r.dte} DTE vs ${thresholds.dteMonitorDays}d escalation`);
  }
  if (!r.likelyPeaked) out.push("peak flag not set");
  if (!r.likelyMature) out.push("not in the maturity window");
  if (!r.hardExitEvaluated) out.push("hard-exit rule not evaluated (no producer)");

  return out;
}
